import { Injectable } from '@angular/core';
import { MigrationArea } from './migration-config.service';
import { goErrorCode } from './go-api-error';
import { environment } from 'src/environments/environment';

export interface DiffLogEntry {
  timestamp: string;
  entity: MigrationArea;
  operation: string;
  entityUid?: string;
  differences: FieldDiff[];
  severity: 'info' | 'warning' | 'error';
}

export interface FieldDiff {
  field: string;
  oldValue: any;
  newValue: any;
}

@Injectable({
  providedIn: 'root'
})
export class DiffLoggerService {

  private logs: DiffLogEntry[] = [];

  compare(entity: MigrationArea, operation: string, oldResult: any, newResult: any, entityUid?: string): void {
    // Disabled by default (see environment.migrationDiffCompareEnabled) — the deep compare over
    // every read result is a real cost on large lists. No-op unless explicitly enabled.
    if (!(environment as any).migrationDiffCompareEnabled) {
      return;
    }

    if (oldResult === undefined || newResult === undefined) {
      this.logEntry(entity, operation, entityUid, [], 'info');
      return;
    }

    const differences = this.deepCompare(oldResult, newResult);
    if (differences.length > 0) {
      this.logEntry(entity, operation, entityUid, differences, 'warning');
    }
  }

  logWriteFailure(entity: MigrationArea, operation: string, error: any): void {
    // The Go lambdas send an errorType code with every error (lambda-conventions.md 3.2);
    // record it so the log can be grouped by code rather than by message text.
    const code = goErrorCode(error);
    console.error(`[Migration] Dual-write FAILURE for ${entity}.${operation} [${code ?? 'no code'}]:`, error?.message ?? error, error);
    this.logs.push({
      timestamp: new Date().toISOString(),
      entity,
      operation,
      differences: [
        { field: 'writeError', oldValue: null, newValue: error?.message ?? String(error) },
        { field: 'errorType', oldValue: null, newValue: code ?? null }
      ],
      severity: 'error'
    });
  }

  logNotMigrated(entity: MigrationArea, operation: string, uid: string): void {
    console.info(`[Migration] Item not yet in new API: ${entity} ${uid} (${operation})`);
    this.logs.push({
      timestamp: new Date().toISOString(),
      entity,
      operation,
      entityUid: uid,
      differences: [{ field: 'notMigrated', oldValue: uid, newValue: null }],
      severity: 'info'
    });
  }

  getLogs(): DiffLogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  private logEntry(entity: MigrationArea, operation: string, entityUid: string | undefined, differences: FieldDiff[], severity: DiffLogEntry['severity']): void {
    const entry: DiffLogEntry = {
      timestamp: new Date().toISOString(),
      entity,
      operation,
      entityUid,
      differences,
      severity
    };
    this.logs.push(entry);

    if (differences.length > 0) {
      console.warn(`[Migration Diff] ${entity}.${operation}${entityUid ? ' uid=' + entityUid : ''}: ${differences.length} difference(s)`, differences);
    }
  }

  private deepCompare(oldVal: any, newVal: any, path: string = ''): FieldDiff[] {
    const diffs: FieldDiff[] = [];

    if (oldVal === newVal) return diffs;
    if (oldVal == null && newVal == null) return diffs;

    if (typeof oldVal !== typeof newVal) {
      // Tolerate null/undefined vs missing
      if ((oldVal == null || oldVal === '') && (newVal == null || newVal === '')) return diffs;
      diffs.push({ field: path || 'root', oldValue: oldVal, newValue: newVal });
      return diffs;
    }

    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      const maxLen = Math.max(oldVal.length, newVal.length);
      for (let i = 0; i < maxLen; i++) {
        diffs.push(...this.deepCompare(oldVal[i], newVal[i], `${path}[${i}]`));
      }
      return diffs;
    }

    if (typeof oldVal === 'object' && oldVal !== null) {
      const allKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal ?? {})]);
      for (const key of allKeys) {
        diffs.push(...this.deepCompare(oldVal[key], newVal?.[key], path ? `${path}.${key}` : key));
      }
      return diffs;
    }

    if (oldVal !== newVal) {
      diffs.push({ field: path || 'root', oldValue: oldVal, newValue: newVal });
    }

    return diffs;
  }
}
